import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import serverURL from '../config';
import CandidatesTable from './CandidatesTable';
import RetestActionsPanel from './RetestActionsPanel';
import { useAuth } from '../context/AuthContext';
import { escapeCsvValue, getCandidateStatusLabel, getCandidateTotalScore, toNumber } from '../utils/reportUtils';


const ConsolidatedReport = () => {
    const { eventId } = useParams();
    const { role } = useAuth();
    const isAdmin = role === 'admin';
    const [report, setReport] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [passPercentage, setPassPercentage] = useState(92);
    const [levelId, setLevelId] = useState(null);
    const [headerActionsNode, setHeaderActionsNode] = useState(null);

    const fetchReport = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${serverURL}/reports/consolidated/${eventId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const eventPassPct = parseFloat(response.data.pass_percentage) || 92;
            const fetchedLevelId = response.data.level_id || null;
            const candidates = response.data.candidates || response.data;

            setPassPercentage(eventPassPct);
            setLevelId(fetchedLevelId);

            setReport(Array.isArray(candidates) ? candidates : []);
        } catch (error) {
            console.error('Error fetching consolidated report:', error);
            toast.error('Failed to load report. Please try again.');
        }
    }, [eventId]);

    useEffect(() => {
        setHeaderActionsNode(document.getElementById('global-header-actions'));
    }, []);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const filteredReport = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return [...report]
            .sort((a, b) => getCandidateTotalScore(b) - getCandidateTotalScore(a))
            .filter((candidate) => {
                if (!normalizedSearch) {
                    return true;
                }

                return [
                    candidate.candidate_name,
                    candidate.candidate_email,
                    candidate.employee_id,
                    candidate.candidate_id,
                ]
                    .filter((value) => value !== null && value !== undefined)
                    .some((value) => String(value).toLowerCase().includes(normalizedSearch));
            });
    }, [report, searchTerm]);

    const handleExport = () => {
        const hasProgramming = filteredReport.some((candidate) => toNumber(candidate.programming_points) > 0);
        const headers = ['Candidate ID', 'Employee ID', 'Candidate Name', 'Email', 'Total Correct', 'Total Attempted'];
        if (hasProgramming) headers.push('Programming Points');
        headers.push('Pass/Fail');

        const csvRows = [headers.map(escapeCsvValue).join(',')];
        filteredReport.forEach(item => {
            const status = getCandidateStatusLabel(item, passPercentage);
            const row = [
                item.candidate_id,
                item.employee_id || '',
                item.candidate_name,
                item.candidate_email,
                item.total_correct,
                item.total_attempted,
            ];
            if (hasProgramming) row.push(item.programming_points);
            row.push(status);
            csvRows.push(row.map(escapeCsvValue).join(','));
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'consolidated_report.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const eligibleCandidates = filteredReport.filter(
        c => c.final_result === 'fail' && c.retest_status === 'none'
    );
    const emptyMessage = report.length === 0
        ? 'No report data available.'
        : 'No candidates match the current search.';

    return (
        <div className="flex flex-col min-h-screen bg-campus-bg">


            <main className="flex-grow p-4 sm:p-6">
                {/* Unified Header Row Portaled into GlobalLayout Back button Wrapper */}
                {headerActionsNode && createPortal(
                    <div className="flex-1 flex items-center justify-between gap-4 w-full">
                        {/* Center: Search Bar */}
                        <div className="flex-1 flex justify-center max-w-lg mx-auto">
                            <input
                                type="text"
                                placeholder="Search by name, email, or ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full text-sm p-2.5 rounded-lg border border-campus-border bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all shadow-sm"
                            />
                        </div>

                        {/* Right: Export Button */}
                        <div className="flex-shrink-0">
                            <button
                                onClick={handleExport}
                                className="bg-campus-text-primary hover:bg-campus-text-body text-white text-sm px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm whitespace-nowrap"
                            >
                                Export as CSV
                            </button>
                        </div>
                    </div>,
                    headerActionsNode
                )}

                <div className="mb-4 flex items-center gap-2">
                    <span className="text-xs font-semibold text-campus-text-body uppercase tracking-wide">Total Candidates</span>
                    <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2.5 rounded-full bg-campus-text-primary text-white text-xs font-bold">{filteredReport.length}</span>
                </div>
                {filteredReport.length > 0 ? (
                    <>
                        {isAdmin && levelId && (
                            <RetestActionsPanel
                                eventId={eventId}
                                levelId={levelId}
                                eligibleCandidates={eligibleCandidates}
                                onAssignmentComplete={fetchReport}
                            />
                        )}
                        <CandidatesTable candidates={filteredReport} passPercentage={passPercentage} eventId={eventId} />
                    </>
                ) : (
                    <p className="text-lg text-campus-text-body">{emptyMessage}</p>
                )}
            </main>
        </div>
    );
};

export default ConsolidatedReport;
